import React, { useState, useRef, useEffect } from "react"
import { Check, ChevronDown, X } from "lucide-react"
import { Label } from "../../../ui/label"
import { Button } from "../../../ui/button"
import { Checkbox } from "../../../ui/checkbox"
import { Input } from "../../../ui/input"
import { cn } from "../../../lib/utils"
import Tooltips from "../Tooltip"

/**
 * MultipleSelectDDL - Multi-select dropdown with search
 * Migrated to use shadcn/ui components
 * 
 * @param {string} title - Field label
 * @param {string} info - Tooltip info text
 * @param {Array} options - Options array: [{ value, label }] or simple array
 * @param {function} onSelect - Selection handler (receives array of selected values)
 * @param {boolean} shouldReset - Reset trigger
 * @param {boolean} onlyOpt - Use simple string values instead of objects
 * @param {Array} initialSelected - Initially selected values
 * @param {boolean} onlyOneSelection - Allow only one selection
 * @param {boolean} isExtended - Extended mode flag
 * @param {boolean} searchable - Show search input (default false)
 * @param {string} size - Trigger button size, forwarded to the Button component (default "default")
 */
const MultipleSelectDDL = ({
  title,
  info,
  options = [],
  onSelect,
  shouldReset,
  onlyOpt,
  initialSelected = [],
  onlyOneSelection,
  isExtended,
  searchable = false,
  size = "default",
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const DdlCloseRef = useRef(null)
  
  const [isChecked, setIsChecked] = useState(() =>
    options?.map((option) =>
      onlyOpt
        ? initialSelected?.includes(option) ?? false
        : initialSelected?.includes(option.value) ?? false
    ) || []
  )
  
  const [selectedValues, setSelectedValues] = useState(initialSelected || [])

  const handleButtonClick = () => {
    setIsOpen(!isOpen)
  }

  const handleClickOutside = (e) => {
    if (DdlCloseRef.current && !DdlCloseRef.current.contains(e.target)) {
      setIsOpen(false)
    }
  }

  const handleOptionClick = (index) => {
    if (onlyOneSelection) {
      // Single selection mode
      const newIsChecked = new Array(isChecked.length).fill(false)
      newIsChecked[index] = true
      setIsChecked(newIsChecked)
      updateSelectedValues(newIsChecked)
      setIsOpen(false)
    } else {
      // Multiple selection mode
      const newIsChecked = [...isChecked]
      newIsChecked[index] = !newIsChecked[index]
      setIsChecked(newIsChecked)
      updateSelectedValues(newIsChecked)
    }
  }

  const handleSelectAll = () => {
    const filteredIndices = filteredOptions?.map((option) => options?.indexOf(option))
    const allFilteredChecked = filteredIndices.every((index) => isChecked[index])
    const newIsChecked = isChecked?.map((_, index) =>
      filteredIndices.includes(index) ? !allFilteredChecked : isChecked[index]
    )
    setIsChecked(newIsChecked)
    updateSelectedValues(newIsChecked)
  }

  const updateSelectedValues = (newIsChecked) => {
    const newSelectedValues = newIsChecked.reduce((acc, checked, i) => {
      if (checked) {
        acc.push(onlyOpt ? options[i] : options[i].value)
      }
      return acc
    }, [])

    onSelect(newSelectedValues)
    setSelectedValues(newSelectedValues)
  }

  useEffect(() => {
    if (initialSelected) {
      setSelectedValues(initialSelected)
      setIsChecked(
        options?.map((option) =>
          onlyOpt
            ? initialSelected.includes(option)
            : initialSelected.includes(option.value)
        ) || []
      )
    }
  }, [initialSelected, options])

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const getOptionLabel = (option) =>
    typeof option === "object" ? (option.label || option.name || String(option.value ?? "")) : String(option ?? "")

  const filteredOptions = options?.filter((option) =>
    getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
  )

  const selectedLabels = onlyOpt
    ? selectedValues.map((value) => (typeof value === "string" ? value : getOptionLabel(value)))
    : options
        ?.filter((item) => selectedValues.includes(item.value))
        ?.map((item) => getOptionLabel(item))

  const onReset = () => {
    setIsChecked(new Array(options?.length).fill(false))
    onSelect([])
    setSelectedValues([])
  }

  useEffect(() => {
    if (shouldReset) {
      onReset()
    }
  }, [shouldReset])

  return (
    <div className="w-full space-y-2">
      {/* Label with Tooltip */}
      {title && (
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {title}
          </Label>
          {info && <Tooltips info={info} />}
        </div>
      )}
      
      <div className="relative w-full" ref={DdlCloseRef}>
        {/* Trigger Button */}
        <Button
          type="button"
          variant="outline"
          size={size}
          role="combobox"
          aria-expanded={isOpen}
          className="w-full justify-between"
          onClick={handleButtonClick}
        >
          <span className="truncate">
            {selectedLabels.length > 0
              ? selectedLabels.length > 3
                ? `${selectedLabels.length} selected`
                : selectedLabels.join(", ")
              : "Select options..."}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80">
          <div className="p-2 space-y-2">
            {/* Search Input — only shown when searchable prop is true */}
            {searchable && options?.length > 0 && (
            <div className="relative">
              <Input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setSearchTerm("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            )}

            {/* Select All */}
            {!onlyOneSelection && filteredOptions?.length > 0 && (
              <div className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded-sm cursor-pointer">
                <Checkbox
                  id="select-all"
                  checked={filteredOptions.every((opt) =>
                    isChecked[options.indexOf(opt)]
                  )}
                  onCheckedChange={handleSelectAll}
                />
                <Label
                  htmlFor="select-all"
                  className="flex-1 cursor-pointer font-medium"
                >
                  Select All
                </Label>
              </div>
            )}

            {/* Options List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions?.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const originalIndex = options.indexOf(option)
                  const isSelected = isChecked[originalIndex]
                  
                  return (
                    <div
                      key={originalIndex}
                      className={cn(
                        "flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded-sm cursor-pointer",
                        isSelected && "bg-accent"
                      )}
                      onClick={() => handleOptionClick(originalIndex)}
                    >
                      <Checkbox
                        id={`option-${originalIndex}`}
                        checked={isSelected}
                        onCheckedChange={() => handleOptionClick(originalIndex)}
                      />
                      <Label
                        htmlFor={`option-${originalIndex}`}
                        className="flex-1 cursor-pointer"
                      >
                        {getOptionLabel(option)}
                      </Label>
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </div>
                  )
                })
              ) : (
                <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                  No options found
                </div>
              )}
            </div>

            {/* Reset Button */}
            {selectedValues.length > 0 && (
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={onReset}
                >
                  Clear Selection
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default MultipleSelectDDL
